'use client'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import useGetProductDetail from '@/hooks/product/use-get-product'
import { sanitizerDescription } from '@/lib/sanitize'
import { NEXT_PUBLIC_LANDINGPAGE_URL } from '@/configs/env'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi
} from "@/components/ui/carousel"
import Autoplay from "embla-carousel-autoplay"

interface ProductDetailProps {
  productId: string
  partnerId?: string
  variantId?: string
  initialQuantity?: number
}

export default function ProductDetail({ productId, partnerId, variantId, initialQuantity = 1 }: ProductDetailProps) {
  const { data: product, isLoading, error, isNotFound } = useGetProductDetail(productId)
  const router = useRouter()
  const [amount, setAmount] = useState(initialQuantity > 0 ? initialQuantity : 1)
  const [selectedVariantId, setSelectedVariantId] = useState<string>('')
  const [caourselapi, setCaourselApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (!caourselapi) {
      return
    }

    setCurrent(caourselapi.selectedScrollSnap())

    caourselapi.on("select", () => {
      setCurrent(caourselapi.selectedScrollSnap())
    })
  }, [caourselapi])

  useEffect(() => {
    if (product?.variants && product.variants.length > 0) {
      if (variantId && product.variants.some((v) => v.id === variantId)) {
        setSelectedVariantId(variantId)
      } else {
        setSelectedVariantId(product.variants[0].id)
      }
    }
  }, [product, variantId])

  useEffect(() => {
    if (partnerId && typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('partner_id', partnerId)
    }
  }, [partnerId])

  const handleQuantityChange = (type: 'increase' | 'decrease') => {
    if (type === 'increase') {
      setAmount((prev) => prev + 1)
    } else if (type === 'decrease' && amount > 1) {
      setAmount((prev) => prev - 1)
    }
  }

  const handleBuyNow = () => {
    const variantId =
      selectedVariantId || (product?.variants && product.variants.length > 0 ? product.variants[0].id : '')

    const searchParams = new URLSearchParams({
      product_id: product?.customId || '',
      variant_id: variantId,
      quantity: amount.toString(),
    })

    router.push(`/dat-hang?${searchParams.toString()}`)
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-300 aspect-square rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-8 bg-gray-300 rounded"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-6 bg-gray-300 rounded w-1/2"></div>
              <div className="h-20 bg-gray-300 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isNotFound) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="text-center py-16">
          <div className="mb-6">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-4xl text-gray-400">📦</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Sản phẩm không tồn tại</h1>
          <p className="text-gray-600 mb-6">
            Sản phẩm với mã <span className="font-mono bg-gray-100 px-2 py-1 rounded">{productId}</span> không được tìm
            thấy.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <div className="space-y-3">
              <Link
                href={NEXT_PUBLIC_LANDINGPAGE_URL}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                ← Quay lại trang chủ
              </Link>
              <div>
                <Link href={NEXT_PUBLIC_LANDINGPAGE_URL} className="text-blue-600 hover:text-blue-800 transition-colors underline">
                  Xem tất cả sản phẩm
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="text-center py-16">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Có lỗi xảy ra</h1>
          <p className="text-gray-600 mb-4">Không thể tải thông tin sản phẩm</p>
          {process.env.NODE_ENV === 'development' && (
            <Link
              href={NEXT_PUBLIC_LANDINGPAGE_URL}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ← Quay lại trang chủ
            </Link>
          )}
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="text-center py-16">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Sản phẩm không tồn tại</h1>
          <p className="text-gray-600 mb-4">Không tìm thấy sản phẩm với ID: {productId}</p>
          {process.env.NODE_ENV === 'development' && (
            <Link
              href={NEXT_PUBLIC_LANDINGPAGE_URL}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ← Quay lại trang chủ
            </Link>
          )}
        </div>
      </div>
    )
  }

  const productDetailsContent = (
    <div className="space-y-6 mt-8">
      {/* 7. Mô tả sản phẩm - full view */}
      {product?.description && (
        <div className="pt-4 border-t border-gray-100">
          <h3 className="text-black text-lg font-semibold mb-3">Mô tả sản phẩm</h3>
          <div
            className="text-gray-600 leading-relaxed text-sm"
            dangerouslySetInnerHTML={{ __html: sanitizerDescription(product?.description || '') }}
          />
        </div>
      )}

      {/* 8. About sections - cũng thu gọn xuống dưới */}
      {product?.about && product.about.length > 0 && (
        <div className="pt-4 border-t border-gray-100">
          <h3 className="text-black text-lg font-semibold mb-3">Thông tin chi tiết</h3>
          <div className="space-y-4">
            {product.about.map((about, index) => (
              <div key={about.id || index}>
                {about.title && <h4 className="font-medium mb-1 text-sm">{about.title}</h4>}
                {about.content && <p className="text-gray-600 text-sm">{about.content}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="max-w-7xl mx-auto p-4 pb-40 lg:pb-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Carousel
            setApi={setCaourselApi}
            opts={{ loop: true }}
            plugins={[
              Autoplay({
                delay: 5000,
              }),
            ]}
            className="relative aspect-square rounded-2xl overflow-hidden bg-gray-50 group/carousel border border-gray-100"
          >
            <CarouselContent>
              {product?.images?.map((image, index) => (
                <CarouselItem key={index}>
                  <img
                    src={image?.origin || ''}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {product?.images && product.images.length > 1 && (
              <>
                <CarouselPrevious className="left-4 opacity-0 group-hover/carousel:enabled:opacity-100 disabled:hidden transition-opacity duration-300 bg-white/80 backdrop-blur-sm border-none shadow-lg hover:bg-white" />
                <CarouselNext className="right-4 opacity-0 group-hover/carousel:enabled:opacity-100 disabled:hidden transition-opacity duration-300 bg-white/80 backdrop-blur-sm border-none shadow-lg hover:bg-white" />
              </>
            )}
          </Carousel>

          {/* Image Thumbnails Gallery */}
          {product?.images && product.images.length > 1 && (
            <div className="relative group">
              <div className="flex gap-2 py-2 px-1">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => caourselapi?.scrollTo(index)}
                    className={`relative flex-1 min-w-0 aspect-square rounded-md overflow-hidden border-2 transition-all duration-300 hover:scale-105
                      ${current === index
                        ? 'border-blue-600 ring-2 ring-blue-50 z-10'
                        : 'border-gray-100 opacity-60 hover:opacity-100 hover:border-blue-200'
                      }`}
                  >
                    <img
                      src={image?.origin || ''}
                      alt={`${product.title} - ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {current === index && (
                      <div className="absolute inset-0 bg-blue-600/5 pointer-events-none" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* {product.galleries && product.galleries.length > 0 && !product.images?.some(img => product.galleries?.some(g => g.imageUrl === img.origin)) && (
            <div className="pt-4">
              <h4 className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wider">Bộ sưu tập</h4>
              <div className="grid grid-cols-4 gap-2">
                {product.galleries.slice(0, 4).map((gallery, index) => (
                  <div key={gallery.id || index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-50 group cursor-zoom-in">
                    {gallery.imageUrl && (
                      <img
                        src={gallery.imageUrl}
                        alt={gallery.caption || `Gallery ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )} */}

        </div>

        {/* Product Info */}
        <div className="space-y-4">
          {/* 1. Brand + Tên */}
          <div>
            {product.brandName && (
              <p className="text-violet-800 font-semibold text-sm mb-1">
                {product.brandName}
              </p>
            )}
            <h1 className="text-3xl font-bold text-gray-900">{product.title}</h1>
          </div>

          {/* 2. Giá */}
          <div className="space-y-1">
            {(() => {
              const selectedVariant = product.variants?.find((v) => v.id === selectedVariantId)
              const displayPrice = selectedVariant?.price ?? product.price
              const displayRegularPrice = selectedVariant?.regularPrice ?? product.regularPrice
              return (
                <>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-bold text-red-600">{displayPrice.toLocaleString('vi-VN')}đ</span>
                    {displayRegularPrice > displayPrice && (
                      <span className="text-xl text-gray-500 line-through">
                        {displayRegularPrice.toLocaleString('vi-VN')}đ
                      </span>
                    )}
                  </div>
                  <div className="text-green-600 font-medium text-sm h-5">
                    {displayRegularPrice > displayPrice && (
                      <>
                        Tiết kiệm: {(displayRegularPrice - displayPrice).toLocaleString('vi-VN')}đ (
                        {(((displayRegularPrice - displayPrice) / displayRegularPrice) * 100).toFixed(0)}%)
                      </>
                    )}
                  </div>
                </>
              )
            })()}
          </div>

          {/* 3. Reviews */}
          {product?.totalReviews > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={`text-lg ${i < product?.reviewStatFiveScale ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
                ))}
              </div>
              <span className="text-gray-600 text-sm">({product?.totalReviews} đánh giá)</span>
            </div>
          )}

          {/* 4. Variants - ưu tiên lên cao */}
          {product?.variants && product.variants.length > 1 && (
            <div>
              <h3 className="text-black text-base font-semibold mb-2">Loại:</h3>
              <div className="space-y-2">
                {product.variants.map((variant) => {
                  const hasDiscount = variant.regularPrice > variant.price
                  const discountPercent = hasDiscount
                    ? Math.round(((variant.regularPrice - variant.price) / variant.regularPrice) * 100)
                    : 0
                  const isSelected = selectedVariantId === variant.id
                  return (
                    <div
                      key={variant.id}
                      onClick={() => setSelectedVariantId(variant.id)}
                      className={`flex justify-between items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${isSelected
                        ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                        : 'border-gray-200 hover:border-violet-300'
                        }`}
                    >
                      <div className="min-w-0">
                        <h4 className="font-medium text-black truncate">{variant.title}</h4>
                        {variant.tagName && (
                          <span
                            className="inline-block px-2 py-0.5 text-xs rounded text-white mt-1"
                            style={{ backgroundColor: variant.tagColor || '#3B82F6' }}
                          >
                            {variant.tagName}
                          </span>
                        )}
                      </div>
                      <div className="text-right shrink-0 flex flex-col items-end gap-0.5">
                        {hasDiscount && (
                          <span
                            className={`text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded tracking-wide ${discountPercent > 30 ? 'animate-deal-pulse' : ''
                              }`}
                          >
                            -{discountPercent}%
                          </span>
                        )}
                        <span className="font-bold text-gray-900">{variant.price.toLocaleString('vi-VN')}đ</span>
                        {hasDiscount && (
                          <span className="text-xs text-gray-400 line-through">
                            {variant.regularPrice.toLocaleString('vi-VN')}đ
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* 5. Quantity */}
          <div className="hidden lg:block">
            <h3 className="text-black text-base font-semibold mb-2">Số lượng</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleQuantityChange('decrease')}
                disabled={amount <= 1}
                className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span className="text-lg font-medium">−</span>
              </button>
              <div className="w-16 h-10 border border-gray-300 rounded-lg flex items-center justify-center">
                <span className="font-medium">{amount}</span>
              </div>
              <button
                onClick={() => handleQuantityChange('increase')}
                className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <span className="text-lg font-medium">+</span>
              </button>
            </div>
          </div>

          {/* Tổng cộng - desktop */}
          <div className="hidden lg:block">
            {(() => {
              const selectedVariant = product.variants?.find((v) => v.id === selectedVariantId)
              const unitPrice = selectedVariant?.price ?? product.price
              const regularPrice = selectedVariant?.regularPrice ?? product.regularPrice
              const discount = Math.max(regularPrice - unitPrice, 0)
              const total = unitPrice * amount
              return (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-baseline justify-between">
                    <span className="text-base font-semibold text-gray-900">Tổng cộng</span>
                    <span className="text-2xl font-bold text-red-600">{total.toLocaleString('vi-VN')}đ</span>
                  </div>
                  <div className="text-right text-green-600 font-medium text-sm mt-1 h-5">
                    {discount > 0 && <>Tiết kiệm: {(discount * amount).toLocaleString('vi-VN')}đ</>}
                  </div>
                </div>
              )
            })()}
          </div>

          {/* 6. Button Mua ngay */}
          <div className="hidden lg:block pt-2">
            <Button
              onClick={handleBuyNow}
              className="w-full py-4 px-6 rounded-lg font-semibold text-lg cursor-pointer text-white bg-linear-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 shadow-md shadow-indigo-500/20 transition-all"
            >
              Mua ngay
            </Button>
            {product?.variants && product.variants.length === 1 && (
              <div className="mt-3 text-sm text-gray-600 text-center">Phiên bản: {product?.variants[0].title}</div>
            )}
            {product?.variants && product.variants.length > 1 && selectedVariantId && (
              <div className="mt-3 text-sm text-gray-600 text-center">
                Đã chọn: {product?.variants.find((v) => v.id === selectedVariantId)?.title}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mô tả sản phẩm - full width */}
      {productDetailsContent}

      {/* Mobile sticky action bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]">
        {(() => {
          const selectedVariant = product.variants?.find((v) => v.id === selectedVariantId)
          const variantTitle = selectedVariant?.title ?? product.variants?.[0]?.title
          const unitPrice = selectedVariant?.price ?? product.price
          const regularPrice = selectedVariant?.regularPrice ?? product.regularPrice
          const discount = Math.max(regularPrice - unitPrice, 0)
          const total = unitPrice * amount
          return (
            <div className="px-4 pt-3 pb-2 space-y-1">
              {variantTitle && (
                <div className="text-sm font-medium text-gray-900 truncate">{variantTitle}</div>
              )}
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex flex-col min-w-0">
                  <span className="text-base font-bold text-red-600">
                    {unitPrice.toLocaleString('vi-VN')}đ
                  </span>
                  {discount > 0 && (
                    <span className="text-xs text-gray-400 line-through truncate">
                      {regularPrice.toLocaleString('vi-VN')}đ
                    </span>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-base font-bold text-gray-900">
                    {total.toLocaleString('vi-VN')}đ
                  </div>
                  {discount > 0 && (
                    <div className="text-xs font-medium text-green-600">
                      Tiết kiệm: {(discount * amount).toLocaleString('vi-VN')}đ
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })()}
        <div className="flex items-center gap-3 px-3 pb-3">
          <div className="flex items-center shrink-0">
            <button
              onClick={() => handleQuantityChange('decrease')}
              disabled={amount <= 1}
              className="w-9 h-10 border border-gray-300 rounded-l-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span className="text-lg font-medium">−</span>
            </button>
            <div className="w-10 h-10 border-y border-gray-300 flex items-center justify-center">
              <span className="font-medium">{amount}</span>
            </div>
            <button
              onClick={() => handleQuantityChange('increase')}
              className="w-9 h-10 border border-gray-300 rounded-r-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <span className="text-lg font-medium">+</span>
            </button>
          </div>
          <Button
            onClick={handleBuyNow}
            className="flex-1 h-10 rounded-lg font-semibold cursor-pointer text-white bg-linear-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 shadow-md shadow-indigo-500/20"
          >
            Mua ngay
          </Button>
        </div>
      </div>
    </div>
  )
}